import React, { FC, useCallback, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwDialog from '../../../components/TwDialog/TwDialog';
import TwSelect from '../../../components/TwSelects/TwSelects';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import { PatternFormat } from 'react-number-format';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import { CompaniesEntities } from '../Companies';
import { useFormik } from 'formik';
import { PAY_RATE_TYPES, initialCompanyValues } from '../CompaniesModal/TwCompaniesModal';
import {
  useGetCarrierByIdQuery,
  useUpdateCarrierMutation
} from '../../../redux/api/carriers/carrierApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import {
  useGetCustomerByIdQuery,
  useUpdateCustomerMutation
} from '../../../redux/api/customer/customerApi';
import useTwLocation from '../../../hooks/useTwLocation';
import {
  useGetDivisionByIdQuery,
  useUpdateDivisionMutation
} from '../../../redux/api/divisions/divisionsApi';
import TwWarning from '../../../components/TwWarning/TwWarning';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
import { Characters, useUnformatValue } from '../../../hooks/useAllowCharacters';
import { CarrierPayRateType } from '../../../redux/api/carriers/carriers.model';
import { CreateClientData } from '../../../redux/api/customer/customer.model';
import { IUpdateDivisionData } from '../../../redux/api/divisions/divisions.model';
import CreateLocation from '../../Locations/CreateLocation/CreateLocation';
import { LoadLocation } from '../../../redux/api/load/load.model';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import { CrossBorderDivisionFields } from '../components/CrossBorderDivisionFields';
import { validateCCC, validateScac } from '../../../utils/manifest/manifestUtils';

interface TwEditCompaniesModalProps {
  open: boolean;
  onClose: () => void;
  entity: CompaniesEntities;
  entityId: string;
  onSuccess?: (response: any) => void;
}

const TwEditCompaniesModal: FC<TwEditCompaniesModalProps> = ({
  open,
  entity,
  entityId,
  onClose,
  onSuccess
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const { enableBorderConnect, enableShowCustomerPreferredCurrencyCode } = useFeatureFlags();
  const [location, setLocation] = useState<TwAutocompleteOption>({ id: '', label: '', value: '' });
  const [locations, setLocations] = useState<TwAutocompleteOption[]>([]);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [isCreateLocationOpen, setIsCreateLocationOpen] = useState<boolean>(false);
  const { locations: twLocations, fetchLocations } = useTwLocation(true);
  const { addNotification } = useNotifications();
  // RTK Queries for fetching data: carriers, customers, divisions
  const { data: carrierData, error: errorCarrier } = useGetCarrierByIdQuery(
    entityId && open && entity === CompaniesEntities.carrier ? entityId : skipToken
  );
  const { data: customerData, error: errorCustomer } = useGetCustomerByIdQuery(
    entityId && open && entity === CompaniesEntities.customer ? entityId : skipToken
  );
  const { data: divisionData, error: errorDivisions } = useGetDivisionByIdQuery(
    entityId && open && entity === CompaniesEntities.division ? entityId : skipToken
  );
  const [updateCarrier] = useUpdateCarrierMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [updateDivision] = useUpdateDivisionMutation();
  const unformatString = useUnformatValue(Characters.number);

  const getCompanyData = useCallback((): void => {
    if (entity === CompaniesEntities.carrier && carrierData) {
      setFieldValue('company_name', carrierData.name || '');
      setFieldValue('company_email', carrierData.email_address || '');
      setFieldValue('company_phone', carrierData.phone || '');
      setFieldValue('pay_rate_type', carrierData.default_pay_rate_type || '');
      setFieldValue('pay_rate', carrierData.pay_rate || '');
      setFieldValue('first_name', carrierData.first_name || '');
      setFieldValue('last_name', carrierData.last_name || '');
      setFieldValue('phone_ext', carrierData.phone_ext || '');
      setFieldValue('external_company_id', carrierData.external_company_id || '');
      setFieldValue('mc_number', carrierData.mc_number || '');
      setFieldValue('tax_id', carrierData.tax_id || '');
      setFieldValue('pay_to', carrierData.pay_to || '');

      setLocation({
        id: carrierData.location?.id || '',
        label: carrierData.location?.name || '',
        value: carrierData.location || ''
      });

      // setFieldValue('phone_ext', carrierData?.phoneExt); not in response api
    } else if (entity === CompaniesEntities.customer && customerData) {
      setFieldValue('company_name', customerData.name || '');
      setFieldValue('company_email', customerData.billing_email || '');
      setFieldValue('company_phone', customerData.primary_phone || '');
      setFieldValue('phone_ext', customerData.phone_ext || '');
      setFieldValue('external_customer_id', customerData.external_customer_id || '');
      setLocation({
        id: customerData.location_id || '',
        label: customerData.location?.name || '',
        value: customerData.location
      });
      setFieldValue('note', customerData.note || null);
      setFieldValue('preferences', customerData.preferences || null);
      setFieldValue('currency_preference', customerData.currency_preference || null);
    } else if (entity === CompaniesEntities.division && divisionData) {
      setFieldValue('company_name', divisionData.name || '');
      setFieldValue('company_email', divisionData.email_address || '');
      setFieldValue('company_phone', divisionData.phone || '');
      setFieldValue('phone_ext', divisionData.phone_ext || '');
      setFieldValue('dot_number', divisionData.dot_number || '');
      setFieldValue('scac', divisionData.scac || '');
      setFieldValue('ca_carrier_code', divisionData.ca_carrier_code || '');
      setLocation({
        id: divisionData.location_id || '',
        label: divisionData.location?.name || '',
        value: divisionData.location
      });
    }
  }, [entity, carrierData, customerData, divisionData]);

  const { values, handleChange, handleSubmit, resetForm, setFieldValue } = useFormik({
    initialValues: initialCompanyValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateCompanyData();
    }
  });

  const close = (): void => {
    resetForm();
    setLocation({ id: '', label: '', value: '' });
    onClose();
  };

  useEffect(() => {
    if (open && entityId) {
      resetForm();
      getCompanyData();
    }
  }, [open, entityId, getCompanyData]);

  useEffect(() => {
    if (open) {
      fetchLocations();
    }
  }, [open]);

  useEffect(() => {
    if (errorCarrier || errorCustomer || errorDivisions) {
      addNotification({
        variant: 'error',
        message: `Error at loading ${entity} data, please try again`
      });
    }
  }, [errorCarrier, errorCustomer, errorDivisions]);

  useEffect(() => {
    const locations = [...twLocations];
    const newLocations: TwAutocompleteOption[] = locations.map((l) => ({
      id: l.id,
      label: l.name || '',
      subLabel: l.full_address
        ? l.full_address
        : l.address_1 && l.address_2
        ? `${l.address_1}, ${l.address_2}`
        : l.address_1,
      value: l || ''
    }));

    setLocations(newLocations);
  }, [twLocations]);

  const updateCompanyData = (): void => {
    if (!values.company_name?.trim()) {
      addNotification({
        variant: 'error',
        message: 'Company name is required'
      });
      return;
    }

    if (values.scac.length && !validateScac(values.scac)) {
      addNotification({
        variant: 'error',
        message: 'SCAC code must be 4 letters'
      });
      return;
    }

    if (values.ca_carrier_code.length && !validateCCC(values.ca_carrier_code)) {
      addNotification({
        variant: 'error',
        message: 'CA Carrier Code must start with a number and be 4 characters in length'
      });
      return;
    }

    switch (entity) {
      case CompaniesEntities.carrier:
        updateCarrier({
          name: values.company_name?.trim(),
          email: values.company_email?.trim(),
          phone: unformatString(values.company_phone),
          phone_ext: values.phone_ext,
          payRate: values.pay_rate,
          mcNumber: values.mc_number,
          taxId: values.tax_id,
          externalCompanyId: values.external_company_id?.trim(),
          payTo: values.pay_to?.trim(),
          defaultPayRateType: values.pay_rate_type as CarrierPayRateType,
          pocFirstname: values.first_name?.trim(),
          pocLastname: values.last_name?.trim(),
          id: entityId,
          locationId: location.id ?? null
        })
          .unwrap()
          .then((updated) => {
            onSuccess && onSuccess(updated);
            onClose();
          })
          .catch((error) => {
            logRocketTrack('Carrier Management', {
              action: 'Edit Carrier',
              severity: 'high',
              tenant_id: userData?.tenant?.id,
              company_name: userData?.tenant?.company_name,
              email: userData?.email_address,
              user_first_name: userData?.first_name,
              user_last_name: userData?.last_name
            });
            addNotification({
              message: error?.data?.message ?? 'Error updating carrier, please try again',
              variant: 'error'
            });
          });
        break;
      case CompaniesEntities.customer:
        const params: CreateClientData = {
          ...customerData,
          companyName: values.company_name?.trim(),
          billingEmail: values.company_email?.trim(),
          primaryPhone: unformatString(values.company_phone),
          companyId: entityId,
          phoneExt: values.phone_ext,
          externalCustomerId: values.external_customer_id,
          note: values.note,
          preferences: values.preferences,
          currencyPreference: values.currency_preference
        };

        params.locationId = location.id ?? null;

        updateCustomer(params)
          .unwrap()
          .then((clientResponse) => {
            onSuccess && onSuccess(clientResponse);
            onClose();
          })
          .catch((error) => {
            console.error(error);
            addNotification({
              message: error?.data?.message ?? 'Error at updating customer info, please try again',
              variant: 'error'
            });
          });
        break;
      case CompaniesEntities.division:
        const paramsDivision: IUpdateDivisionData = {
          id: entityId,
          name: values.company_name?.trim(),
          email_address: values.company_email?.trim(),
          phone: unformatString(values.company_phone),
          phone_ext: values.phone_ext,
          dot_number: values.dot_number.trim() ?? null,
          scac: values.scac.trim() ?? null,
          ca_carrier_code: values.ca_carrier_code.trim() ?? null
        };

        paramsDivision.location_id = location.id ?? null;

        updateDivision(paramsDivision)
          .unwrap()
          .then((divisionResponse: any) => {
            if (divisionResponse.error) {
              addNotification({
                message:
                  divisionResponse?.error?.data?.message ??
                  'Error at updating division info, please try again',
                variant: 'error'
              });
            }
            onSuccess && onSuccess(divisionResponse);
            onClose();
          })
          .catch((error) => {
            addNotification({
              message: error?.data?.message ?? 'Error at updating division info, please try again',
              variant: 'error'
            });
            onClose();
          });
        break;
    }
  };

  const handleCreateLocationClose = (): void => {
    setIsCreateLocationOpen(false);
  };

  const handleCreateLocationSuccess = (newLocation: LoadLocation): void => {
    const newAutocompleteOption: TwAutocompleteOption = {
      id: newLocation.id,
      label: newLocation.name,
      value: newLocation
    };
    setLocation(newAutocompleteOption);
    setLocations((prevLocations) => [newAutocompleteOption, ...prevLocations]);
    fetchLocations();
  };

  const renderCarrierFields = (): React.ReactNode => {
    return (
      <Grid container columnSpacing={2}>
        <Grid item xs={12} md={6}>
          <TwSelect
            sx={{ marginBottom: '2rem' }}
            id="pay_rate_type"
            name="pay_rate_type"
            value={values.pay_rate_type}
            optionList={PAY_RATE_TYPES}
            onInputChange={handleChange}
            label="Default pay rate type"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TwField
            id="payRate"
            name="pay_rate"
            value={values.pay_rate}
            label="Rate"
            inputType={TwInputTypes.number}
            onInputChange={handleChange}
            startAdorment={
              values.pay_rate_type !== 'PERCENTAGE' ? (
                <span className="create-carrier__rate-adornment">$</span>
              ) : null
            }
            endAdorment={
              values.pay_rate_type === 'PERCENTAGE' ? (
                <span className="create-carrier__rate-adornment">%</span>
              ) : null
            }
            className="create-carrier__pay-rate"
          />
        </Grid>
        <Grid item xs={12}>
          <span className="create-carrier__rate-help">
            Any driver created under that carrier will have this rate as default
          </span>
        </Grid>
      </Grid>
    );
  };

  const renderCustomerFields = (): React.ReactNode => {
    return (
      <Grid container columnSpacing={2}>
        <Grid item xs={12} md={6}>
          <TwField
            id="external_customer_id"
            name="external_customer_id"
            value={values.external_customer_id}
            label="Customer ID (optional)"
            placeholder=""
            inputType={TwInputTypes.text}
            onInputChange={handleChange}
          />
        </Grid>
        {enableShowCustomerPreferredCurrencyCode && (
          <Grid item xs={12} md={6}>
            <TwSelect
              id="currency_preference"
              name="currency_preference"
              value={values.currency_preference}
              label="Currency"
              onInputChange={handleChange}
              optionList={[
                { label: 'USD', value: 'USD' },
                { label: 'CAD', value: 'CAD' }
              ]}
            />
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <>
      <TwDialog
        open={open && !showWarning}
        onClickClose={close}
        title={entity === CompaniesEntities.division ? 'Edit division' : 'Edit company'}
        actions={[
          <TwButton key="1" type={TwButtonType.secondary} label="Cancel" action={close} />,
          <TwButton key="2" type={TwButtonType.primary} label="Save" action={handleSubmit} />
        ]}
        draggable
      >
        {entity !== CompaniesEntities.division && (
          <Grid container columnSpacing={2}>
            <Grid item xs={12} md={6}>
              <TwField
                sx={{ '& .MuiInputBase-input': { textTransform: 'capitalize' } }}
                id="companyType"
                name="company_type"
                value={entity}
                disabled
                label="Type"
              />
            </Grid>
          </Grid>
        )}

        <Grid container columnSpacing={2}>
          <Grid item xs={12} md={6}>
            <TwField
              id="company_name"
              name="company_name"
              inputType={TwInputTypes.text}
              label={entity === CompaniesEntities.division ? 'Division name' : 'Company name'}
              onInputChange={handleChange}
              value={values.company_name}
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={2}>
          <Grid item xs={12} md={6}>
            <TwField
              id="company_mail"
              name="company_email"
              inputType={TwInputTypes.text}
              label={
                entity === CompaniesEntities.division
                  ? 'Email (optional)'
                  : 'Company email (optional)'
              }
              value={values.company_email}
              onInputChange={handleChange}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            className="tid-create-client__company-phone 
                         create-client__phone-input-container"
          >
            <PatternFormat
              format="(###) ###-####"
              id="company_phone"
              name="company_phone"
              value={values.company_phone}
              placeholder="(___) ___-____"
              customInput={TwField}
              onInputChange={handleChange}
              displayType="input"
              label="Phone number (optional)"
              className="create-client__phone create-client__phone__no-right-border"
            />

            <TwField
              id="phone_ext"
              name="phone_ext"
              value={values.phone_ext}
              label=""
              placeholder="Ext."
              inputType={TwInputTypes.text}
              onInputChange={handleChange}
              className="create-client__extension create-client__extension__left-border"
            />
          </Grid>
        </Grid>
        {entity === CompaniesEntities.carrier && renderCarrierFields()}
        <Grid container>
          <Grid item xs={12}>
            <TwAutocomplete
              id="companyLocation"
              name="companyLocation"
              label="Location"
              optionList={locations}
              filterOptions={(x: any): any => x}
              value={location.label}
              onChange={(e, value): void => {
                if (value === null) {
                  setLocation({ id: '', label: '', value: '' });
                  return;
                }
                if (value) setLocation(value);
              }}
              onInputChange={(e, value): void => {
                fetchLocations(value ?? undefined);
              }}
              renderOption={(props, option): React.ReactNode => {
                return <RenderOption props={props} option={option} />;
              }}
              listAction={{
                label: 'New location',
                action: (): void => setIsCreateLocationOpen(true)
              }}
              disableNoOption
              enableClearIcon
            />
          </Grid>
        </Grid>
        {entity === CompaniesEntities.customer && renderCustomerFields()}
        {entity === CompaniesEntities.carrier && (
          <>
            <Grid container columnSpacing={2}>
              <Grid item xs={12} md={6}>
                <TwField
                  id="mc_number"
                  name="mc_number"
                  inputType={TwInputTypes.number}
                  label="MC Number (optional)"
                  value={values.mc_number}
                  onInputChange={handleChange}
                  onlyNumbers
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TwField
                  id="tax_id"
                  name="tax_id"
                  inputType={TwInputTypes.number}
                  label="Tax ID (optional)"
                  value={values.tax_id}
                  onInputChange={handleChange}
                  onlyNumbers
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TwField
                  id="external_company_id"
                  name="external_company_id"
                  inputType={TwInputTypes.text}
                  label="Carrier ID (optional)"
                  value={values.external_company_id}
                  onInputChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TwField
                  id="pay_to"
                  name="pay_to"
                  inputType={TwInputTypes.text}
                  label="Pay to (optional)"
                  value={values.pay_to}
                  onInputChange={handleChange}
                />
              </Grid>
            </Grid>
            <Grid container columnSpacing={2}>
              <Grid item xs={12}>
                <span className="create-carrier__poc-title tid-create-carrier__poc-title">
                  Point of contact (optional)
                </span>
              </Grid>
              <Grid item xs={12} md={6}>
                <TwField
                  id="first_name"
                  name="first_name"
                  inputType={TwInputTypes.text}
                  label="First name"
                  value={values.first_name}
                  onInputChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TwField
                  id="last_name"
                  name="last_name"
                  inputType={TwInputTypes.text}
                  label="Last name"
                  value={values.last_name}
                  onInputChange={handleChange}
                />
              </Grid>
            </Grid>
          </>
        )}
        {entity === CompaniesEntities.division && enableBorderConnect && (
          <CrossBorderDivisionFields handleChange={handleChange} values={values} />
        )}
      </TwDialog>
      <TwWarning
        warningTitle={`Editing ${entity} on all orders`}
        warningMessage={`By editing this ${entity} (${values?.company_name}), you will be editing the ${entity} on all orders with it assigned. Do you want to
      continue?`}
        open={open}
        handleClose={close}
        showWarning={showWarning}
        setShowWarning={setShowWarning}
        reloadId={entityId}
      />
      <CreateLocation
        open={isCreateLocationOpen}
        onClose={handleCreateLocationClose}
        onSuccess={handleCreateLocationSuccess}
      />
    </>
  );
};

export default TwEditCompaniesModal;
